import React from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  FaMoneyBillWave, 
  FaSignOutAlt, 
  FaUser, 
  FaHome,
  FaReceipt,
  FaChartBar
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const LayoutContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(246, 139, 33, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(20, 19, 64, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(246, 139, 33, 0.02) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
`;

const Header = styled.header`
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  padding: 1.5rem 2rem;
  box-shadow: 0 8px 32px rgba(246, 139, 33, 0.25);
  position: sticky;
  top: 0;
  z-index: 1000;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
      linear-gradient(90deg, rgba(20, 19, 64, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.6rem;
  font-weight: 800;
  position: relative;
  z-index: 1;
  
  .logo-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(20, 19, 64, 0.2);
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 8px;
    }
    
    &:hover {
      transform: scale(1.05);
      background: rgba(255, 255, 255, 0.25);
      box-shadow: 0 6px 20px rgba(20, 19, 64, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    
    .logo-icon {
      width: 40px;
      height: 40px;
    }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .user-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  
  &:hover {
    background: rgba(20, 19, 64, 0.2);
    border-color: rgba(20, 19, 64, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(20, 19, 64, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    
    span {
      display: none;
    }
  }
`;

const Navigation = styled.nav`
  background: white;
  border-bottom: 2px solid #f8f9fa;
  padding: 1rem 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 999;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, rgba(246, 139, 33, 0.02) 0%, rgba(20, 19, 64, 0.01) 100%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    justify-content: space-between;
  }
`;

const NavItem = styled.li`
  .nav-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #495057;
    text-decoration: none;
    padding: 1rem 0;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    border-bottom: 3px solid transparent;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #F68B21 0%, #141340 100%);
      transform: scaleX(0);
      transition: transform 0.3s ease;
    }
    
    &:hover {
      color: #141340;
      
      &::before {
        transform: scaleX(1);
      }
    }
    
    &.active {
      color: #141340;
      
      &::before {
        transform: scaleX(1);
      }
    }
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      padding: 0.75rem 0;
      gap: 0.5rem;
    }
  }
`;

const MainContent = styled.main`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 200px); /* Account for sticky header and nav */
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    min-height: calc(100vh - 180px);
  }
`;

const CashierLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActiveRoute = (path: string) => {
    return window.location.pathname === path;
  };

  return (
    <LayoutContainer>
      <Header>
        <HeaderContent>
          <Logo>
            <div className="logo-icon">
              <img src="/pfrs.jpeg" alt="PFRS Logo" />
            </div>
            Cashier Portal
          </Logo>
          <UserSection>
            <div className="user-info">
              <IconWrapper icon={FaUser} />
              {user?.name}
            </div>
            <LogoutButton onClick={handleLogout}>
              <IconWrapper icon={FaSignOutAlt} />
              <span>Logout</span>
            </LogoutButton>
          </UserSection>
        </HeaderContent>
      </Header>

      <Navigation>
        <NavList>
          <NavItem>
            <Link 
              to="/cashier" 
              className={`nav-link ${isActiveRoute('/cashier') ? 'active' : ''}`}
            >
              <IconWrapper icon={FaHome} />
              <span>Dashboard</span>
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/cashier/expenses" 
              className={`nav-link ${isActiveRoute('/cashier/expenses') ? 'active' : ''}`}
            >
              <IconWrapper icon={FaReceipt} />
              <span>Expenses</span>
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/cashier/fund-requests" 
              className={`nav-link ${isActiveRoute('/cashier/fund-requests') ? 'active' : ''}`}
            >
              <IconWrapper icon={FaMoneyBillWave} />
              <span>Fund Requests</span>
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/cashier/reports" 
              className={`nav-link ${isActiveRoute('/cashier/reports') ? 'active' : ''}`}
            >
              <IconWrapper icon={FaChartBar} />
              <span>Reports</span>
            </Link>
          </NavItem>
        </NavList>
      </Navigation>

      <MainContent>
        <Outlet />
      </MainContent>
    </LayoutContainer>
  );
};

export default CashierLayout; 